import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FormContext } from "./Form";
import { FormController, FormState, FormValidator } from "./types";
import { getValidationErrors } from "./utils";
import { isMandatory } from "./validators/isMandatoryValidator";

export type InputProps<T> = UseInputParams<T>;

export interface UseInputParams<T> {
  name: string;
  isRequired?: boolean;
  validators?: FormValidator[];
  initialValue?: T | null;
  cleanStateOnUnmount?: boolean;
}

export const useInput = <T>({
  name,
  validators,
  isRequired,
  initialValue,
  cleanStateOnUnmount,
}: UseInputParams<T>): UseInputResponse<T> => {
  const { t } = useTranslation();
  const [formState, setFormState] = useContext<FormController>(FormContext);
  const [value, setValue] = useState<T | null>(
    initialValue !== undefined ? initialValue : formState[name]?.value,
  );

  const setIsDirtyToTrueIfItHasChanged = () => {
    if (!formState[name]?.isDirty) {
      setFormState(name, {
        ...formState[name],
        isDirty: true,
      });
    }
  };

  const isDirty: boolean = useMemo(() => {
    return formState[name]?.isDirty;
  }, [formState]);

  const onBlurHandler = () => {
    setIsDirtyToTrueIfItHasChanged();
  };

  const onChangeHandler = useCallback(
    (value: T | null) => {
      setValue(value);

      if (!formState[name] || value !== formState[name]?.value) {
        const newFormState =
          (JSON.parse(JSON.stringify(formState)) as FormState) || {};
        newFormState[name] = {
          ...newFormState[name],
          value,
        };

        const errors = getValidationErrors(
          isRequired ? [...(validators || []), isMandatory] : validators,
          value,
          newFormState,
        );

        setFormState(name, {
          value: value,
          isDirty: true,
          errors: errors.map((e) => t(e)),
          isValid:
            errors.length === 0 && !(isRequired && value === initialValue),
          isRequired,
        });
      }
    },
    [
      value,
      formState,
      initialValue,
      isRequired,
      name,
      setFormState,
      t,
      validators,
    ],
  );

  //clean up
  useEffect(() => {
    return () => {
      if (cleanStateOnUnmount) {
        setFormState(name, undefined);
      }
    };
  }, [cleanStateOnUnmount]);

  useEffect(() => {
    const hasValueChanged = value !== formState[name]?.value;
    const hasRequiredChanged =
      isRequired !== undefined && isRequired !== formState[name]?.isRequired;

    //initialize the input
    if (!formState[name] || hasValueChanged || hasRequiredChanged) {
      const newFormState =
        (JSON.parse(JSON.stringify(formState)) as FormState) || {};
      newFormState[name] = {
        ...newFormState[name],
        value,
      };

      const errors = getValidationErrors(
        isRequired
          ? [
              //if isRequired is true, add the isMandatory validator to the list of validators
              ...(validators?.filter(
                (validator) => validator.error !== isMandatory.error,
              ) || []),
              isMandatory,
            ]
          : validators,
        value,
        newFormState,
      );

      setFormState(name, {
        value: value,
        isDirty: value !== initialValue,
        errors: errors.map((e) => t(e)),
        isValid: errors.length === 0,
        isRequired: !!isRequired,
      });
    }
  }, [
    value,
    formState,
    initialValue,
    isRequired,
    name,
    setFormState,
    t,
    validators,
  ]);

  return {
    value,
    errors: formState[name]?.errors ?? [],
    isDirty,
    isRequired,
    onBlurHandler,
    onChangeHandler,
  };
};

interface UseInputResponse<T> {
  value: T | null;
  errors: string[];
  isDirty: boolean;
  isRequired?: boolean;
  onBlurHandler: () => void;
  onChangeHandler: (value: T | null) => void;
}
